import { useEffect, useState } from 'react';
import { getDomain } from '@/lib/utils';
import { useStoreInfo } from '@/lib/store';

interface Product {
  images: string[];
}

export function MaintenancePage() {
  const { storeInfo } = useStoreInfo();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [productImages, setProductImages] = useState<string[]>([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const domain = getDomain();
        const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/api/v1/products/visible?domain=${domain}`);

        if (!response.headers.get('content-type')?.includes('application/json')) {
          throw new Error('Products API returned non-JSON response');
        }

        const data = await response.json();

        if (data.status && data.products) {
          const images = data.products
            .filter((product: Product) => product.images && product.images.length > 0)
            .map((product: Product) => product.images[0]);
          setProductImages(images);
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
      }
    };

    fetchProducts();
  }, []);



  useEffect(() => {
    if (productImages.length === 0) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [productImages.length]);

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-rose-950 via-red-950 to-black overflow-y-auto overflow-x-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,_rgba(255,255,255,0.14),_transparent_40%),radial-gradient(circle_at_80%_30%,_rgba(255,255,255,0.10),_transparent_40%)]" />
      <div className="absolute inset-0 overflow-visible">
        {productImages.length > 0 && (
          <img
            src={productImages[currentImageIndex]}
            alt="Current product"
            className="absolute inset-0 w-full h-full object-cover opacity-10 blur-sm"
          />
        )}

        {productImages.slice(0, 9).map((image, index) => (
          <div
            key={`art-${index}`}
            className="absolute rounded-2xl shadow-lg border border-white/20"
            style={{
              width: 120,
              height: 120,
              top: `${10 + (index * 8)}%`,
              left: `${5 + (index * 10)}%`,
              opacity: 0.12,
              transform: `rotate(${(index % 3) * 8 - 10}deg)`
            }}
          >
            <img
              src={image}
              alt="Featured product"
              className="w-full h-full object-cover rounded-2xl"
              style={{ filter: 'grayscale(60%) contrast(0.8)' }}
            />
          </div>
        ))}
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-[100vh] py-8 px-4 sm:px-6">
        <div className="w-full max-w-4xl bg-white/10 backdrop-blur-xl border border-rose-500/30 rounded-3xl shadow-2xl p-6 sm:p-10 md:p-12">
          {storeInfo?.site_logo && (
            <div className="flex justify-center mb-6 sm:mb-8">
              <img
                src={`http://leeyahtrend.com.ng/${storeInfo.site_logo}`}
                alt={storeInfo.site_name || 'Store logo'}
                className="h-16 sm:h-24 md:h-32 object-contain"
              />
            </div>
          )}

          <div className="flex flex-col gap-6 sm:gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight">
                Store temporarily paused
              </h1>
              <p className="mt-3 text-lg sm:text-xl text-rose-100 max-w-2xl">
                Quick restock in progress. Orders will resume once inventory is updated.
              </p>
            </div>

    
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: '1. Audit inventory', desc: 'Confirm stock levels across all warehouses.' },
              { title: '2. Refresh listings', desc: 'Update descriptions, prices and images.' },
              { title: '3. Enable checkout', desc: 'Open store only after quality pass.' }
            ].map((tile) => (
              <div key={tile.title} className="bg-white/10 border border-rose-300/20 rounded-2xl p-5 backdrop-blur-sm">
                <h3 className="text-md text-rose-100 font-bold">{tile.title}</h3>
                <p className="mt-2 text-sm text-rose-200">{tile.desc}</p>
              </div>
            ))}
          </div>


          <div className="mt-8 sm:mt-10 flex justify-center">
            <button
              type="button"
              className="inline-flex items-center justify-center px-6 py-3 bg-white/10 hover:bg-white/20 text-rose-100 rounded-full font-medium border border-rose-300"
              onClick={() => window.location.reload()}
            >
              Check status
            </button>
          </div>

          <p className="mt-6 text-xs text-rose-200 text-center">
            Thank you for your patience - we’re making sure every order ships perfectly.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes progress {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(0); }
        }
        .animate-progress {
          animation: progress 3.5s ease infinite;
        }
      `}</style>
    </div>
  );
}


